if (process.browser) {
  window.labeler?.init(__webpack_share_scopes__.default);
}
;
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Hydrate, QueryClientProvider } from 'react-query';
import { DefaultSeo } from 'next-seo';
import type { OpenGraphMedia } from 'next-seo/lib/types';
import dynamic from 'next/dynamic';
import getConfig from 'next/config';
import Head from 'next/head';
import Nprogress from 'nprogress';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import 'styles/global.css';
import 'react-image-crop/dist/ReactCrop.css';
import { createApiErrorSubject } from 'utils/request/clarifaiFetch';
import { SSRProvider } from '@react-aria/ssr';
import { makeAppQueryClient } from 'utils/apiCache/queryClient';
import { ReCaptchaContext } from 'modules/ReCaptchaContext';
import type { RequestWatcherProps } from 'modules/Auth/RequestWatcher';
import { LazyToastContainer } from 'components/ToastMessage/LazyToastContainer';
import { CoreProviders } from 'components/CoreProviders';
import { AnalyticsCollectionProvider } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { AuthProviderProps } from 'modules/Auth/AuthContext';
import { SidebarProvider } from 'components/SidebarV2/SidebarContext';
import { usePageTrack } from '../hooks/usePageTrack';
const RequestWatcher = dynamic<RequestWatcherProps>(() => import('modules/Auth/RequestWatcher').then(mod => mod.RequestWatcher), {
  ssr: false
});
const ModalManager = dynamic<unknown>(() => import('components/ModalManager').then(mod => mod.ModalManager), {
  ssr: false
});
const AuthProvider = dynamic<AuthProviderProps>(() => import('modules/Auth/AuthContext').then(mod => mod.AuthProvider), {
  ssr: false
});
const ModalProvider = dynamic<{
  animated: boolean;
  children: ReactNode;
}>(() => import('components/ModalManager/Context').then(mod => mod.ModalProvider), {
  ssr: false
});
Nprogress.configure({
  showSpinner: false
});
function App({
  Component,
  pageProps,
  router
}: Record<string, any>): import('react').ReactElement {
  useProgress();
  const queryClientRef = useRef(makeAppQueryClient());
  const {
    publicRuntimeConfig
  } = getConfig();
  const siteKey = publicRuntimeConfig.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY || null;
  const isNotProd = publicRuntimeConfig.CF_ENV !== 'production';
  useEffect(() => {
    if (!siteKey) {
      return () => null;
    }
    const RECAPTCHA_HIDE_TIMEOUT = 5000;
    const hideRecatchaBadgeWithDelay = () => {
      const grecaptchaBadge = (document.querySelector('.grecaptcha-badge') as HTMLDivElement);
      if (grecaptchaBadge && grecaptchaBadge.style) {
        clearTimeout(timeout);
        grecaptchaBadge.style.visibility = 'hidden';
      } else {
        timeout = setTimeout(() => hideRecatchaBadgeWithDelay(), RECAPTCHA_HIDE_TIMEOUT);
      }
    };
    let timeout = setTimeout(() => hideRecatchaBadgeWithDelay(), RECAPTCHA_HIDE_TIMEOUT);
    return () => {
      clearTimeout(timeout);
    };
  }, [siteKey]);
  const apiErrorSubject = useMemo(createApiErrorSubject, []);
  // temporary effect to let `clarifaiFetch` access apiErrorSubject; will later be removed in favor
  // of passing apiErrorSubject from React to the API Client
  useEffect(() => {
    window.__CF__API__ERROR__SUBJECT = apiErrorSubject;
    return () => {
      window.__CF__API__ERROR__SUBJECT = undefined;
    };
  }, []);
  const stream = useMemo(() => apiErrorSubject.asObservable(), []);
  usePageTrack();
  const defaultSeoProps: {
    title: string;
    description?: string;
  } = {
    title: pageProps?.seoProps?.title || "Clarifai - The World's AI"
  };
  if (pageProps?.seoProps?.description) {
    defaultSeoProps.description = pageProps.seoProps.description;
  }
  let openGraphImage: OpenGraphMedia = {
    url: 'https://www.clarifai.com/hubfs/community-the-worlds-ai-community-featured-2024.png',
    width: 1200,
    height: 600,
    alt: "The World's AI Community: Vision, Language, Audio and AutoML"
  };
  if (pageProps?.seoProps?.image?.url) {
    openGraphImage = (pageProps?.seoProps?.image as OpenGraphMedia);
  }
  return <SSRProvider>
      <Head>
        <link rel="canonical" href={router.asPath.split('?')[0]} key="canonical" />
      </Head>
      <DefaultSeo {...defaultSeoProps} additionalLinkTags={[{
      rel: 'icon',
      href: '/favicon.svg'
    }]} openGraph={{
      url: `https://www.clarifai.com${router.pathname}`,
      images: [openGraphImage]
    }} additionalMetaTags={[{
      name: 'viewport',
      content: 'width=device-width,initial-scale=1'
    }]} dangerouslySetAllPagesToNoIndex={isNotProd} dangerouslySetAllPagesToNoFollow={isNotProd} />
      <QueryClientProvider client={queryClientRef.current}>
        <Hydrate state={pageProps.dehydratedState}>
          <ReCaptchaContext reCaptchaKey={siteKey}>
            <AnalyticsCollectionProvider trackEvent={pageProps?.trackEventProps}>
              <ModalProvider animated>
                <AuthProvider initialAuthData={pageProps?.defaultProps?.currentUser} intercomUserJWT={(pageProps?.defaultProps?.intercomUserJWT as string | undefined)}>
                  <CoreProviders currentUser={pageProps?.defaultProps?.currentUser} enabledFeatures={pageProps?.defaultProps?.enabledFeatures || []}>
                    <SidebarProvider>
                      {/* Redirect user to Settings page if their email is not validated */}
                      <RequestWatcher stream={stream} />
                      <Component {...pageProps} />

                      <LazyToastContainer style={{
                      zIndex: 10000012
                    }} />
                      <ModalManager />
                    </SidebarProvider>
                  </CoreProviders>
                </AuthProvider>
              </ModalProvider>
            </AnalyticsCollectionProvider>
          </ReCaptchaContext>
        </Hydrate>
      </QueryClientProvider>
    </SSRProvider>;
}
const handleRouteChangeStart = (): void => {
  Nprogress.start();
};
const handleRouteChangeEnd = (): void => {
  Nprogress.done();
};
function useProgress(): null {
  const router = useRouter();
  useEffect(() => {
    if (process.env.NODE_ENV !== 'test') {
      router.events.on('routeChangeStart', handleRouteChangeStart);
      router.events.on('routeChangeComplete', handleRouteChangeEnd);
      router.events.on('routeChangeError', handleRouteChangeEnd);
      return () => {
        router.events.off('routeChangeStart', handleRouteChangeStart);
        router.events.off('routeChangeComplete', handleRouteChangeEnd);
        router.events.off('routeChangeError', handleRouteChangeEnd);
      };
    }
    return () => {};
  }, []);
  return null;
}
export default App;